/**
 * B606 (d) — the storefront ticket page (`/t#<token>[,<token>…]`), opened from
 * the ticket email's one button or a card's own link. Built in the hosted
 * quote page's mould (B549 (7)): a seller bar, then the tickets — each with
 * its QR (drawn from the token the buyer already holds), label, departure and
 * status — then the order's summary ONCE. NO FRAME YET — redline pending;
 * the design side measures it.
 */
import type { ReactElement } from "react";
import { Banner, Button, EmptyState, Skeleton } from "@transact/ui";
import { formatMoney } from "../lib/money.js";
import { moreTicketsInEmail, scannable, ticketStatusWord, type TicketFace, type TicketPageAnswer } from "../lib/ticketPage.js";

export interface TicketPageProps {
  readonly tickets: readonly TicketFace[];
  readonly loading?: boolean;
  /** The ticket service could not be reached (not a token that missed). */
  readonly error?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
}

const plural = (n: number, one: string, many: string): string => `${String(n)} ${n === 1 ? one : many}`;

/** What a ticket that will not scan says instead of its QR. */
function notScannableWords(status: string): string {
  if (status === "VOIDED") return "Cancelled — this ticket won't scan.";
  if (status === "EXHAUSTED") return "Used — this ticket has already been scanned.";
  if (status === "EXPIRED") return "Expired — this ticket is past its date.";
  return "This ticket can't be scanned.";
}

function TicketCard(props: { readonly face: TicketFace; readonly page: TicketPageAnswer }): ReactElement {
  const { page } = props;
  const word = ticketStatusWord(page.status);
  return (
    <li className="tp-ticket">
      {scannable(page.status) && props.face.qr !== undefined ? (
        <img className="tp-qr" src={props.face.qr} alt={`QR code for ${page.ticket.label}`} width={240} height={240} />
      ) : (
        <p className="tp-unscannable">{notScannableWords(page.status)}</p>
      )}
      <div className="tp-ticket-facts">
        <p className="tp-label">{page.ticket.label}</p>
        {page.ticket.bill !== undefined ? <p className="tp-bill">{page.ticket.bill}</p> : null}
        {page.ticket.when !== undefined ? <p className="tp-when">{page.ticket.when}</p> : null}
        <p className={`tp-status tp-status--${scannable(page.status) ? "live" : "spent"}`}>{word}</p>
      </div>
    </li>
  );
}

function Summary(props: { readonly order: NonNullable<TicketPageAnswer["order"]> }): ReactElement {
  const { order } = props;
  return (
    <section className="tp-summary" aria-labelledby="tp-summary-title">
      <h2 className="tp-summary-title" id="tp-summary-title">
        Your order
      </h2>
      <ul className="tp-summary-lines">
        {order.lines.map((l) => (
          <li key={l.label}>
            <span>{`${l.label} × ${String(l.quantity)}`}</span>
            <span>{formatMoney(l.net)}</span>
          </li>
        ))}
        {order.fees.map((f) => (
          <li key={f.label}>
            <span>{f.label}</span>
            <span>{formatMoney(f.amount)}</span>
          </li>
        ))}
        {order.tax !== undefined ? (
          <li>
            <span>Tax</span>
            <span>{formatMoney(order.tax)}</span>
          </li>
        ) : null}
      </ul>
      {order.total !== undefined ? (
        <p className="tp-total">
          <span>Total paid</span>
          <span>{formatMoney(order.total)}</span>
        </p>
      ) : null}
    </section>
  );
}

export function TicketPage(props: TicketPageProps): ReactElement {
  const opened = props.tickets.filter((t): t is TicketFace & { page: TicketPageAnswer } => t.page !== undefined);
  const missed = props.tickets.length - opened.length;
  const first = opened[0]?.page;
  const seller = first?.seller;

  if (opened.length === 0) {
    return (
      <main className="tp tp--bare" aria-label="Your tickets">
        <div className="tp-bare-body">
          {props.loading === true ? (
            <div role="status" aria-label="Opening your tickets">
              <Skeleton lines={6} />
            </div>
          ) : props.error !== undefined ? (
            <Banner tone="warn">
              {props.error}
              {props.onRetry !== undefined ? (
                <Button variant="textlink" onClick={props.onRetry}>
                  Try again
                </Button>
              ) : null}
            </Banner>
          ) : (
            <EmptyState
              title="This link doesn't open your tickets"
              body="Check that the whole link from your ticket email reached this page — some mail apps cut long links. Your QR codes are also in the email itself, so you can show those at the gate."
            />
          )}
        </div>
      </main>
    );
  }

  const order = opened.find((t) => t.page.order !== undefined)?.page.order;
  const orderQuantity = order?.lines.reduce((n, l) => n + l.quantity, 0) ?? 0;
  const more = moreTicketsInEmail(orderQuantity, props.tickets.length);

  return (
    <main className="tp" aria-label="Your tickets">
      <header className="tp-bar">
        <p className="tp-brand">{(seller ?? "Your tickets").toUpperCase()}</p>
        {order?.confirmationCode !== undefined ? <p className="tp-ref">{`CONFIRMATION ${order.confirmationCode}`}</p> : null}
      </header>
      <div className="tp-body">
        <h1 className="tp-title">{opened.length === 1 ? "Your ticket" : "Your tickets"}</h1>
        <p className="tp-lede">Show each QR code at the gate — one scan per ticket. Turn your screen brightness up if it won't read.</p>
        {missed > 0 ? (
          <Banner tone="warn">{`${plural(missed, "ticket", "tickets")} in this link didn't open. The QR codes are also in your ticket email${seller !== undefined ? ` from ${seller}` : ""}.`}</Banner>
        ) : null}
        <ol className="tp-tickets">
          {opened.map((t) => (
            <TicketCard key={t.token} face={t} page={t.page} />
          ))}
        </ol>
        {more > 0 ? <p className="tp-more">{`${plural(more, "more ticket is", "more tickets are")} in your email.`}</p> : null}
        {order !== undefined ? <Summary order={order} /> : null}
      </div>
    </main>
  );
}
